import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { 
  ArrowLeft, Mail, Phone, Building2, Briefcase, Trash2, Save, 
  Share2, Copy, ExternalLink, X, BellRing, BellOff, Eye, Mic, MapPin, Check, Sparkles
} from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../lib/api";
import TagPicker, { tagClass } from "../components/TagPicker";
import ConfirmDialog from "../components/ConfirmDialog";
import VoiceRecorder from "../components/VoiceRecorder";
import { deleteLocalLead, saveLocalLead } from "../lib/leadCache";
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function fmtRelative(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const ms = Date.now() - d.getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
}

function toDatetimeLocalValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function LeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [tags, setTags] = useState([]);
  const [notes, setNotes] = useState("");
  const [reminder, setReminder] = useState("");
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmNote, setConfirmNote] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const shareUrl = useMemo(() => {
    return lead?.share_token ? `https://expolead.thensgroup.in/share/${lead.share_token}` : "";
  }, [lead?.share_token]);

  const imageSrc = useMemo(() => {
    if (!lead?.image_base64) return null;
    return lead.image_base64.startsWith('data:') 
      ? lead.image_base64 
      : `data:image/jpeg;base64,${lead.image_base64}`;
  }, [lead?.image_base64]);

  const copyTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const handleCopy = async (text, key) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success(`Copied ${key}`);
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      toast.error(`Failed to copy ${key}`);
    }
  };

  const openNativeShare = async () => {
    if (!shareUrl) return;

    if (Capacitor.isNativePlatform()) {
      try {
        await Share.share({
          title: `${lead.name || "Contact"} — digital card`,
          text: `${lead.name || "Contact"}${lead.company ? " · " + lead.company : ""}`,
          url: shareUrl,
          dialogTitle: 'Share Lead Card',
        });
      } catch (err) {
        console.log("Native share cancelled or failed", err);
      }
    } else if (navigator.share) {
      try {
        await navigator.share({
          title: `${lead.name || "Contact"} — digital card`,
          text: `${lead.name || "Contact"}${lead.company ? " · " + lead.company : ""}`,
          url: shareUrl,
        });
      } catch {
        /* user cancelled */
      }
    } else {
      setShareOpen(true);
    }
  };

  const copyShare = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
      setForm({
        name: data.name || "",
        company: data.company || "",
        job_title: data.job_title || "",
        email: data.email || "",
        email2: data.email2 || "",
        phone: data.phone || "",
        phone2: data.phone2 || "",
        address: data.address || "",
        website: data.website || "",
      });
      setTags(data.tags || []);
      setNotes(data.notes || "");
      setReminder(toDatetimeLocalValue(data.reminder_at));
      
      // Update local storage record with full details
      await saveLocalLead(data);
    } catch {
      toast.error("Lead not found");
      navigate("/dashboard", { replace: true });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, tags, notes };
      if (reminder) {
        const remDate = new Date(reminder);
        if (!isNaN(remDate.getTime())) {
          payload.reminder_at = remDate.toISOString();
        } else {
          payload.clear_reminder = true;
        }
      } else {
        payload.clear_reminder = true;
      }
      const { data } = await api.patch(`/leads/${id}`, payload);
      setLead(data);
      setEditing(false);
      
      // Immediate IndexedDB cache synchronization
      await saveLocalLead(data);

      toast.success("Lead updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const askRemove = () => setConfirmOpen(true);
  const doRemove = async () => {
    setConfirmOpen(false);
    try {
      await api.delete(`/leads/${id}`);
      await deleteLocalLead(id);
      toast.success("Lead deleted");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    }
  };

  const handleVoiceAdded = (note) => {
    setLead((cur) => {
      if (!cur) return cur;
      const updated = { ...cur, voice_notes: [...(cur.voice_notes || []), note] };
      saveLocalLead(updated).catch(() => {});
      return updated;
    });
  };

  const askDeleteVoice = (note) => setConfirmNote(note);
  const doDeleteVoice = async () => {
    const note = confirmNote;
    setConfirmNote(null);
    if (!note) return;
    try {
      await api.delete(`/leads/${id}/voice/${note.id}`);
      setLead((cur) => {
        const updated = {
          ...cur,
          voice_notes: (cur.voice_notes || []).filter((n) => n.id !== note.id),
        };
        saveLocalLead(updated).catch(() => {});
        return updated;
      });
      toast.success("Voice note removed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
    }
  };

  const clearReminder = async () => {
    try {
      const { data } = await api.patch(`/leads/${id}`, { clear_reminder: true });
      setLead(data);
      setReminder("");
      await saveLocalLead(data);
      toast.success("Reminder cleared");
    } catch (err) {
      toast.error("Could not clear reminder");
    }
  };

  if (loading || !lead || !form) {
    return (
      <div className="px-5 pt-8 pb-32 max-w-md mx-auto animate-pulse font-sans">
        <div className="h-7 w-24 bg-blue-100/50 rounded-full mb-6" />
        <div className="h-48 bg-white/70 rounded-3xl mb-4 border border-blue-100/50" />
        <div className="h-7 w-1/2 bg-blue-100/50 rounded-xl mb-2" />
        <div className="h-4 w-1/3 bg-blue-100/40 rounded-lg" />
      </div>
    );
  }

  return (
    <div className="pb-36 px-4 pt-3 max-w-md mx-auto font-sans antialiased text-slate-900 relative min-h-screen" data-testid="lead-detail">
      {/* Ambient Blue Glows */}
      <div className="fixed top-0 left-1/2 -translate-x-1/2 w-full max-w-md h-72 bg-gradient-to-b from-blue-500/15 via-sky-400/10 to-transparent blur-3xl pointer-events-none -z-10" />
      <div className="fixed bottom-32 right-0 w-60 h-60 bg-blue-600/10 blur-[100px] pointer-events-none -z-10" />

      {/* Floating Header */}
      <div className="flex items-center justify-between py-3 mb-2 sticky top-0 z-30 bg-slate-50/75 backdrop-blur-2xl border-b border-white/60">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-white/80 hover:bg-white text-xs font-bold text-slate-700 hover:text-blue-600 border border-slate-200/80 shadow-xs active:scale-95 transition-all"
          data-testid="back-to-list"
        >
          <ArrowLeft className="h-3.5 w-3.5 text-blue-600" /> Leads
        </Link>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShareOpen(true)}
            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-blue-50/90 hover:bg-blue-100/80 text-xs font-bold text-blue-700 border border-blue-200/80 shadow-xs active:scale-95 transition-all"
            data-testid="open-share-button"
          >
            <Share2 className="h-3.5 w-3.5 text-blue-600" /> Share
          </button>
          <button
            onClick={askRemove}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/80 hover:bg-red-50 text-xs font-bold text-red-600 border border-slate-200/80 hover:border-red-200 shadow-xs active:scale-95 transition-all"
            data-testid="delete-lead-button"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Scanned Card Image */}
      {imageSrc && (
        <div className="p-1.5 rounded-3xl bg-white/85 backdrop-blur-xl border border-white/90 shadow-[0_8px_30px_rgba(37,99,235,0.08)] mb-5 overflow-hidden">
          <img
            src={imageSrc}
            alt="card"
            className="w-full rounded-[22px] object-cover max-h-56 shadow-xs"
            data-testid="lead-image"
            loading="lazy"
            decoding="async"
          />
        </div>
      )}

      {/* Header Info */}
      <div className="px-1.5 pt-1 pb-3">
        <h1 
          className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-950"
          data-testid="lead-name"
        >
          {lead.name || "Unnamed Contact"}
        </h1>
        
        {(lead.job_title || lead.company) && (
          <div className="flex flex-wrap items-center gap-2 text-xs font-semibold mt-2">
            {lead.job_title && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-white/80 border border-slate-200/80 text-slate-700 backdrop-blur-md shadow-xs">
                <Briefcase className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                <span>{lead.job_title}</span>
              </span>
            )}
            {lead.company && (
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-blue-50/90 border border-blue-200/80 text-blue-800 backdrop-blur-md shadow-xs">
                <Building2 className="h-3.5 w-3.5 text-blue-600 shrink-0" />
                <span>{lead.company}</span>
              </span>
            )}
          </div>
        )}

        {Array.isArray(lead.tags) && lead.tags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3.5" data-testid="lead-tags-display">
            {lead.tags.filter(Boolean).map((t) => (
              <span key={t} className={`px-3 py-0.5 rounded-full text-[11px] font-bold border backdrop-blur-md shadow-xs ${tagClass(t)}`}>
                {t}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Contact Fields */}
      <div className="flex flex-col gap-2.5 mt-2">
        {lead.email && (
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-[0_4px_16px_rgba(37,99,235,0.05)] hover:border-blue-200 transition-all">
            <div className="flex items-center gap-3.5 min-w-0 pr-2">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-blue-600 to-sky-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/25">
                <Mail className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-500">Email</p>
                <a 
                  href={`mailto:${lead.email}`} 
                  className="text-[14px] font-semibold text-slate-900 hover:text-blue-600 truncate block tracking-tight"
                  data-testid="lead-email"
                >
                  {lead.email}
                </a>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(lead.email, "email")}
              className="p-2 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-blue-50/50 transition active:scale-95"
              title="Copy email"
            >
              {copiedKey === "email" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        )}

        {lead.phone && (
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-[0_4px_16px_rgba(37,99,235,0.05)] hover:border-blue-200 transition-all">
            <div className="flex items-center gap-3.5 min-w-0 pr-2">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-blue-600 to-sky-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/25">
                <Phone className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-600">Mobile</p>
                <a 
                  href={`tel:${lead.phone}`} 
                  className="text-[14px] font-semibold text-slate-900 hover:text-blue-600 truncate block tracking-tight tabular-nums"
                  data-testid="lead-phone"
                >
                  {lead.phone}
                </a>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(lead.phone, "phone")}
              className="p-2 text-slate-400 hover:text-blue-600 rounded-lg hover:bg-blue-50/50 transition active:scale-95"
              title="Copy phone"
            >
              {copiedKey === "phone" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        )}

        {lead.email2 && (
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-xs">
            <div className="flex items-center gap-3.5 min-w-0 pr-2">
              <div className="h-9 w-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                <Mail className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Secondary Email</p>
                <a href={`mailto:${lead.email2}`} className="text-[14px] font-medium text-slate-900 truncate block tracking-tight">
                  {lead.email2}
                </a>
              </div>
            </div>
            <button type="button" onClick={() => handleCopy(lead.email2, "email2")} className="p-2 text-slate-400 hover:text-blue-600 transition">
              {copiedKey === "email2" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        )}

        {lead.phone2 && (
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-xs">
            <div className="flex items-center gap-3.5 min-w-0 pr-2">
              <div className="h-9 w-9 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                <Phone className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Secondary Phone</p>
                <a href={`tel:${lead.phone2}`} className="text-[14px] font-medium text-slate-900 truncate block tracking-tight tabular-nums">
                  {lead.phone2}
                </a>
              </div>
            </div>
            <button type="button" onClick={() => handleCopy(lead.phone2, "phone2")} className="p-2 text-slate-400 hover:text-blue-600 transition">
              {copiedKey === "phone2" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        )}

        {lead.address && (
          <div className="flex items-start justify-between p-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-[0_4px_16px_rgba(37,99,235,0.05)]">
            <div className="flex items-start gap-3.5 min-w-0 pr-2">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-blue-600 to-sky-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-500/25 mt-0.5">
                <MapPin className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-amber-600">Office Address</p>
                <p className="text-[13px] font-medium leading-relaxed text-slate-800 whitespace-pre-wrap tracking-tight mt-0.5">
                  {lead.address}
                </p>
              </div>
            </div>
            <button type="button" onClick={() => handleCopy(lead.address, "address")} className="p-2 text-slate-400 hover:text-blue-600 transition mt-0.5">
              {copiedKey === "address" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        )}

        {lead.website && (
          <div className="flex items-center justify-between p-3.5 rounded-2xl bg-white/90 backdrop-blur-xl border border-white/90 shadow-[0_4px_16px_rgba(37,99,235,0.05)] hover:border-blue-200 transition-all">
            <div className="flex items-center gap-3.5 min-w-0 pr-2">
              <div className="h-9 w-9 rounded-xl bg-gradient-to-tr from-cyan-600 to-blue-500 text-white flex items-center justify-center shrink-0 shadow-md shadow-cyan-500/25">
                <ExternalLink className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Website</p>
                <a
                  href={lead.website.startsWith("http") ? lead.website : `https://${lead.website}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[13px] font-semibold text-blue-600 hover:underline tracking-tight truncate block"
                >
                  {lead.website}
                </a>
              </div>
            </div>
            <button
              type="button"
              onClick={() => handleCopy(lead.website, "website")}
              className="p-2 text-slate-400 hover:text-blue-600 transition"
              title="Copy website"
            >
              {copiedKey === "website" ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
        )}
      </div>

      {/* Notes */}
      {lead.notes && !editing && (
        <div className="mt-4 p-4 rounded-2xl bg-white/85 backdrop-blur-xl border border-white/90 shadow-[0_4px_16px_rgba(37,99,235,0.04)]">
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-600 mb-1.5 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-blue-600" /> Lead Notes
          </p>
          <p className="text-[13.5px] leading-relaxed text-slate-800 whitespace-pre-wrap tracking-tight" data-testid="lead-notes">{lead.notes}</p>
        </div>
      )}

      {/* Follow-Up Scheduled */}
      {!editing && lead.reminder_at && (
        <div className="mt-4 p-4 border border-blue-200/80 bg-gradient-to-r from-blue-50/90 to-sky-50/70 backdrop-blur-xl rounded-2xl flex items-center justify-between gap-3 shadow-xs" data-testid="reminder-banner">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-md shadow-blue-600/25">
              <BellRing className="h-4 w-4" />
            </div>
            <div>
              <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-700">Follow-up Scheduled</p>
              <p className="text-[13px] font-bold text-slate-900 tracking-tight" data-testid="reminder-banner-time">{fmtDateTime(lead.reminder_at)}</p>
            </div>
          </div>
          <button
            onClick={clearReminder}
            className="text-[11px] font-bold text-slate-600 hover:text-red-600 px-3 py-1.5 rounded-full bg-white/90 border border-blue-200/80 shadow-xs transition active:scale-95"
            data-testid="clear-reminder"
          >
            Clear
          </button>
        </div>
      )}

      {/* Digital Card Views */}
      {!editing && (lead.share_views > 0 || lead.last_viewed_at) && (
        <div className="mt-4 p-3.5 border border-white/90 bg-white/80 backdrop-blur-xl rounded-2xl flex items-center gap-3 shadow-xs" data-testid="share-analytics">
          <div className="h-8 w-8 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 border border-blue-100">
            <Eye className="h-4 w-4" />
          </div>
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400">Digital Card Analytics</p>
            <p className="text-[13px] text-slate-800 font-semibold tracking-tight">
              <strong className="text-blue-600 font-extrabold" data-testid="share-views-count">{lead.share_views}</strong> views
              {lead.last_viewed_at && <span className="font-normal text-slate-500"> · active {fmtRelative(lead.last_viewed_at)}</span>}
            </p>
          </div>
        </div>
      )}

      {/* Voice Notes */}
      {!editing && (
        <div className="mt-6" data-testid="voice-section">
          <p className="text-[11px] font-extrabold uppercase tracking-widest text-slate-500 mb-2.5 px-1 flex items-center gap-1.5">
            <Mic className="w-3.5 h-3.5 text-blue-600" /> Voice Notes
          </p>
          <VoiceRecorder leadId={id} onAdded={handleVoiceAdded} />
          
          {(lead.voice_notes || []).length > 0 && (
            <div className="mt-3 flex flex-col gap-2.5" data-testid="voice-notes-list">
              {lead.voice_notes.map((n) => (
                <div key={n.id} className="p-3.5 border border-white/90 rounded-2xl bg-white/90 backdrop-blur-xl shadow-xs" data-testid={`voice-note-${n.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-blue-600 flex items-center gap-1">
                      <Mic className="h-3 w-3" /> {fmtRelative(n.created_at)}
                    </span>
                    <button
                      onClick={() => askDeleteVoice(n)}
                      className="text-slate-400 hover:text-red-600 p-1"
                      aria-label="Delete voice note"
                      data-testid={`voice-delete-${n.id}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  {n.summary && <p className="text-[13.5px] font-bold text-slate-900">{n.summary}</p>}
                  {n.transcript && <p className="text-xs text-slate-500 italic mt-0.5">"{n.transcript}"</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {!editing && (
        <div className="mt-7 flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="h-12 w-full bg-gradient-to-r from-blue-600 to-sky-600 hover:from-blue-700 hover:to-sky-700 text-white text-sm font-bold rounded-2xl shadow-lg shadow-blue-600/25 transition-all active:scale-[0.98] flex items-center justify-center gap-2 border border-blue-400/30"
            data-testid="share-card-button"
          >
            <Share2 className="h-4 w-4" /> Share Digital Card
          </button>
          
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="h-12 w-full bg-white/90 hover:bg-white text-slate-900 text-sm font-bold rounded-2xl border border-slate-200/90 shadow-xs hover:border-blue-300 transition-all active:scale-[0.98]"
            data-testid="edit-lead-button"
          >
            Edit Details
          </button>
        </div>
      )}

      {/* Edit Form */}
      {editing && (
        <div className="mt-4 p-5 rounded-3xl bg-white/95 backdrop-blur-2xl border border-blue-100 shadow-lg shadow-blue-500/5 flex flex-col gap-4" data-testid="edit-form">
          <EditField label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="edit-name" />
          <EditField label="Job Title" value={form.job_title} onChange={(v) => setForm({ ...form, job_title: v })} testid="edit-job-title" />
          <EditField label="Company" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="edit-company" />
          <EditField label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="edit-email" />
          <EditField label="Secondary Email" type="email" value={form.email2} onChange={(v) => setForm({ ...form, email2: v })} />
          <EditField label="Mobile Phone" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="edit-phone" />         
          <EditField label="Secondary Phone" type="tel" value={form.phone2} onChange={(v) => setForm({ ...form, phone2: v })} />
          <EditField label="Address" type="text" value={form.address} onChange={(v) => setForm({ ...form, address: v })} />
          <EditField label="Website" type="url" value={form.website} onChange={(v) => setForm({ ...form, website: v })} />

          <div>
            <label className="text-[11px] font-extrabold uppercase tracking-wider text-blue-600 mb-1.5 block">Smart Tags</label>
            <TagPicker value={tags} onChange={setTags} />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-extrabold uppercase tracking-wider text-blue-600">Follow-up Reminder</label>
            <div className="flex gap-2">
              <input
                type="datetime-local"
                className="w-full h-11 px-3.5 bg-slate-50/80 border border-slate-200 rounded-xl text-[13px] font-medium text-slate-900 focus:outline-none focus:border-blue-600 focus:bg-white transition"
                value={reminder}
                onChange={(e) => setReminder(e.target.value)}
                data-testid="edit-reminder"
              />
              {reminder && (
                <button
                  type="button"
                  onClick={() => setReminder("")}
                  className="h-11 px-3.5 rounded-xl bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 active:scale-95"
                  data-testid="edit-clear-reminder"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="text-[11px] font-extrabold uppercase tracking-wider text-blue-600 mb-1.5 block">Notes</label>
            <textarea
              className="w-full p-3.5 bg-slate-50/80 border border-slate-200 rounded-xl text-[13px] font-medium text-slate-900 focus:outline-none focus:border-blue-600 focus:bg-white transition min-h-[5.5rem]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="edit-notes"
            />
          </div>

          <div className="flex gap-2.5 pt-2">
            <button
              onClick={() => {
                setEditing(false);
                setForm({
                  name: lead.name || "",
                  company: lead.company || "",
                  job_title: lead.job_title || "",
                  email: lead.email || "",
                  email2: lead.email2 || "",
                  phone: lead.phone || "",
                  phone2: lead.phone2 || "",
                  address: lead.address || "",
                  website: lead.website || "",
                });
                setTags(lead.tags || []);
                setNotes(lead.notes || "");
                setReminder(toDatetimeLocalValue(lead.reminder_at));
              }}
              className="h-12 flex-1 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold text-sm rounded-xl transition active:scale-[0.98]"
              data-testid="cancel-edit-button"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="h-12 flex-1 bg-gradient-to-r from-blue-600 to-sky-600 text-white font-bold text-sm rounded-xl flex items-center justify-center gap-1.5 shadow-md shadow-blue-600/25 active:scale-[0.98] disabled:opacity-60 transition"
              data-testid="save-edit-button"
            >
              <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      )}

      {/* Share Bottom Sheet */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/40 backdrop-blur-md"
          onClick={() => setShareOpen(false)}
          data-testid="share-sheet"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md bg-white/95 backdrop-blur-2xl rounded-t-[32px] p-6 shadow-2xl border-t border-white/80 space-y-4"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-widest text-blue-600">Digital Lead Card</p>
                <h3 className="text-xl font-extrabold text-slate-950 tracking-tight">Share Contact</h3>
              </div>
              <button
                onClick={() => setShareOpen(false)}
                className="h-8 w-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex items-center gap-2 p-2 pl-3.5 rounded-xl bg-slate-50 border border-slate-200/90 shadow-xs">
              <span className="flex-1 text-[13px] text-slate-700 truncate font-medium tracking-tight">
                {shareUrl}
              </span>
              <button
                onClick={copyShare}
                className="h-8 px-3.5 rounded-lg bg-blue-600 text-white text-xs font-bold inline-flex items-center gap-1 active:scale-95 shadow-xs"
              >
                <Copy className="h-3 w-3" /> Copy
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <a
                href={shareUrl}
                target="_blank"
                rel="noreferrer"
                className="h-11 rounded-xl bg-white border border-slate-200 text-slate-900 text-[13px] font-bold flex items-center justify-center gap-1.5 shadow-xs hover:bg-blue-50/50 hover:border-blue-200 active:scale-95 transition"
              >
                <ExternalLink className="h-3.5 w-3.5 text-blue-600" /> Preview
              </a>
              <button
                type="button"
                onClick={openNativeShare}
                className="h-11 rounded-xl bg-slate-950 text-white text-[13px] font-bold flex items-center justify-center gap-1.5 shadow-xs hover:bg-slate-900 active:scale-95 transition"
              >
                <Share2 className="h-3.5 w-3.5 text-blue-400" /> Share…
              </button>
            </div>

            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!lead?.share_token) return;
                const a = document.createElement("a");
                a.href = `${API}/share/${lead.share_token}/vcard`;
                a.target = "_blank";
                document.body.appendChild(a);
                a.click();
                a.remove();
              }}
              className="h-11 w-full rounded-xl bg-blue-50 border border-blue-100 text-blue-700 text-[13px] font-bold flex items-center justify-center gap-1.5 hover:bg-blue-100 active:scale-95 transition"
            >
              <Save className="h-3.5 w-3.5" /> Download .vcf
            </button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Delete this lead?"
        description="The lead, its image, voice notes and share link will be permanently removed."
        confirmLabel="Delete lead"
        onConfirm={doRemove}
        testid="confirm-delete-lead"
      />
      <ConfirmDialog
        open={!!confirmNote}
        onOpenChange={(o) => !o && setConfirmNote(null)}
        title="Remove voice note?"
        description="The transcription and summary will be removed."
        confirmLabel="Remove"
        onConfirm={doDeleteVoice}
        testid="confirm-delete-voice"
      />
    </div>
  );
}

function EditField({ label, value, onChange, type = "text", testid }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[11px] font-extrabold uppercase tracking-wider text-blue-600">{label}</label>
      <input
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 px-3.5 bg-slate-50/80 border border-slate-200 rounded-xl text-[13.5px] font-medium text-slate-900 focus:outline-none focus:border-blue-600 focus:bg-white transition"
        data-testid={testid}
      />
    </div>
  );
}